import { useState, useEffect, useRef, useCallback } from "react";

const FEED_URL = process.env.REACT_APP_FEED_URL || "http://YOUR_DROPLET_IP:3000/feed";

const COLORS = {
  purple: "#9945FF",
  teal: "#14F195",
  purpleGlow: "rgba(153,69,255,0.4)",
  tealGlow: "rgba(20,241,149,0.4)",
  purpleDim: "rgba(153,69,255,0.15)",
  tealDim: "rgba(20,241,149,0.15)",
  chrome: "#C0C0C0",
  black: "#000000",
  darkSurface: "#0a0a0a",
  cardBg: "rgba(255,255,255,0.03)",
  cardBorder: "rgba(255,255,255,0.08)",
};

const MOCK_HOLDERS = [
  "7xKp...3mR9","Bq9f...7tL2","Nm3c...9wE5","Yx5d...4kQ1",
  "Pk2a...8nF7","Rj6h...2vB4","Wz8e...5cA3","Lv4b...1mD6",
  "Ht7g...6pJ8","Cs1k...0xN2","Dm4r...2pQ7","Fx8s...5kB3",
];

function short(addr) {
  if (!addr) return "";
  if (addr.includes("...")) return addr;
  return addr.slice(0, 4) + "..." + addr.slice(-4);
}

function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const fn = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return size;
}

// Single reel strip component
function Reel({ spinning, finalValue, delay, highlight }) {
  const [display, setDisplay] = useState(MOCK_HOLDERS[0]);
  const [isSettling, setIsSettling] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (spinning) {
      setIsSettling(false);
      let i = Math.floor(Math.random() * MOCK_HOLDERS.length);
      ref.current = setInterval(() => {
        setDisplay(MOCK_HOLDERS[i % MOCK_HOLDERS.length]);
        i++;
      }, 60 + delay * 15);
    } else {
      clearInterval(ref.current);
      setTimeout(() => {
        setIsSettling(true);
        setDisplay(finalValue || MOCK_HOLDERS[0]);
      }, delay * 180);
    }
    return () => clearInterval(ref.current);
  }, [spinning, finalValue, delay]);

  const borderColor = highlight ? COLORS.teal : spinning ? COLORS.purple : COLORS.chrome;
  const textColor = highlight ? COLORS.teal : spinning ? COLORS.purple : COLORS.chrome;
  const glowColor = highlight ? COLORS.tealGlow : spinning ? COLORS.purpleGlow : "transparent";

  return (
    <div style={{
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: "clamp(11px, 1.8vw, 14px)",
      color: textColor,
      padding: "10px 14px",
      background: "rgba(0,0,0,0.8)",
      border: `1px solid ${borderColor}`,
      borderRadius: "4px",
      textAlign: "center",
      letterSpacing: "1px",
      boxShadow: `0 0 12px ${glowColor}, inset 0 0 8px rgba(0,0,0,0.8)`,
      transition: "border-color 0.3s, box-shadow 0.3s, color 0.3s",
      minWidth: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      transform: isSettling && !spinning ? "scale(1.02)" : "scale(1)",
    }}>
      {display}
    </div>
  );
}

// Particle burst on win
function Particles({ active }) {
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    if (!active) return;
    const p = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: 30 + Math.random() * 40,
      y: 20 + Math.random() * 60,
      color: Math.random() > 0.5 ? COLORS.purple : COLORS.teal,
      size: Math.random() * 6 + 3,
      vx: (Math.random() - 0.5) * 4,
      vy: (Math.random() - 0.5) * 4,
    }));
    setParticles(p);
    const t = setTimeout(() => setParticles([]), 2000);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 999 }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: p.size,
          height: p.size,
          borderRadius: "50%",
          background: p.color,
          boxShadow: `0 0 6px ${p.color}`,
          animation: "burst 1.8s ease-out forwards",
          "--vx": `${p.vx * 80}px`,
          "--vy": `${p.vy * 80}px`,
        }} />
      ))}
    </div>
  );
}

// Countdown ring
function CountdownRing({ nextDrawTime }) {
  const [timeLeft, setTimeLeft] = useState(300);
  const TOTAL = 300;

  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, Math.floor((nextDrawTime - Date.now()) / 1000));
      setTimeLeft(left);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [nextDrawTime]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const pct = timeLeft / TOTAL;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const urgent = timeLeft <= 30;

  return (
    <div style={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}>
      <svg width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
        <circle
          cx="70" cy="70" r={r} fill="none"
          stroke={urgent ? COLORS.purple : COLORS.teal}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          style={{
            filter: `drop-shadow(0 0 6px ${urgent ? COLORS.purple : COLORS.teal})`,
            transition: "stroke-dasharray 1s linear, stroke 0.5s",
          }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: "28px",
          fontWeight: "700",
          color: urgent ? COLORS.purple : COLORS.teal,
          letterSpacing: "2px",
          textShadow: `0 0 16px ${urgent ? COLORS.purple : COLORS.teal}`,
          lineHeight: 1,
        }}>
          {mins}:{String(secs).padStart(2, "0")}
        </div>
        <div style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: "9px",
          color: "rgba(255,255,255,0.3)",
          letterSpacing: "2px",
          marginTop: "4px",
        }}>
          NEXT DRAW
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [feed, setFeed] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [winnerAddress, setWinnerAddress] = useState(null);
  const [showWinner, setShowWinner] = useState(false);
  const [burst, setBurst] = useState(false);
  const [prevWinCount, setPrevWinCount] = useState(0);
  const [activeTab, setActiveTab] = useState("live");
  const { w } = useWindowSize();
  const isMobile = w < 768;

  // Poll feed
  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await fetch(FEED_URL);
        const data = await res.json();
        setFeed(prev => {
          if (prev && data.recentWins?.length > prevWinCount) {
            triggerWinAnimation(data.lastWinner?.shortWinner || data.lastWinner?.winner);
            setPrevWinCount(data.recentWins.length);
          }
          return data;
        });
      } catch (e) {
        // use mock data if feed unavailable
      }
    };
    fetchFeed();
    const t = setInterval(fetchFeed, 5000);
    return () => clearInterval(t);
  }, [prevWinCount]);

  const triggerWinAnimation = useCallback((winner) => {
    setSpinning(true);
    setShowWinner(false);
    setWinnerAddress(null);
    setTimeout(() => {
      setSpinning(false);
      setTimeout(() => {
        setWinnerAddress(winner);
        setShowWinner(true);
        setBurst(true);
        setTimeout(() => setBurst(false), 100);
      }, 600);
    }, 3200);
  }, []);

  const leaderboard = feed?.leaderboard || [];
  const recentWins = feed?.recentWins || [];
  const potSol = feed?.currentDraw?.potSol || 0;
  const totalPaid = feed?.totalSolPaid || 0;
  const qualifyingHolders = feed?.currentDraw?.qualifyingHolders || 0;
  const nextDrawTime = feed?.currentDraw?.nextDrawTime || Date.now() + 300000;

  // Build reel addresses from leaderboard or mock
  const reelAddresses = leaderboard.length > 0
    ? leaderboard.map(h => h.shortAddress)
    : MOCK_HOLDERS;

  const finalSymbol = winnerAddress || reelAddresses[0];

  return (
    <div style={{
      minHeight: "100vh",
      background: COLORS.black,
      backgroundImage: `
        radial-gradient(ellipse at 15% 50%, rgba(153,69,255,0.08) 0%, transparent 55%),
        radial-gradient(ellipse at 85% 50%, rgba(20,241,149,0.08) 0%, transparent 55%)
      `,
      color: "#fff",
      fontFamily: "'Share Tech Mono', monospace",
      overflowX: "hidden",
    }}>
      <style>{`
        @keyframes burst {
          0% { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(var(--vx), var(--vy)) scale(0); opacity: 0; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        @keyframes winFlash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .tx-row:hover { background: rgba(255,255,255,0.05) !important; }
        .lb-row:hover { background: rgba(255,255,255,0.04) !important; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(153,69,255,0.4); border-radius: 2px; }
      `}</style>

      <Particles active={burst} />

      {/* Scanline overlay */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1,
        overflow: "hidden", opacity: 0.03,
      }}>
        <div style={{
          position: "absolute", left: 0, right: 0, height: "2px",
          background: "rgba(255,255,255,0.8)",
          animation: "scanline 8s linear infinite",
        }} />
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "16px 12px" : "24px 24px" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "clamp(48px, 10vw, 88px)",
            fontWeight: "900",
            letterSpacing: "8px",
            lineHeight: 1,
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, #C77DFF 40%, ${COLORS.teal} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "6px",
          }}>
            $SLOTS
          </div>
          <div style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "clamp(9px, 1.5vw, 11px)",
            letterSpacing: "6px",
            color: "rgba(255,255,255,0.3)",
          }}>
            HOLD ◆ SPIN ◆ WIN
          </div>

          {/* Stats bar */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: isMobile ? 16 : 32,
            marginTop: 16,
            flexWrap: "wrap",
          }}>
            {[
              { label: "POT", value: `${potSol.toFixed(4)} SOL`, color: COLORS.teal },
              { label: "HOLDERS", value: qualifyingHolders, color: COLORS.purple },
              { label: "TOTAL PAID", value: `${totalPaid.toFixed(3)} SOL`, color: COLORS.teal },
              { label: "MIN HOLD", value: "100K", color: COLORS.purple },
            ].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "9px", letterSpacing: "3px", color: "rgba(255,255,255,0.3)", marginBottom: 2 }}>
                  {s.label}
                </div>
                <div style={{
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: "clamp(13px, 2vw, 16px)",
                  fontWeight: "700",
                  color: s.color,
                  textShadow: `0 0 12px ${s.color}`,
                }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tab nav */}
        <div style={{ display:"flex", justifyContent:"center", gap:4, marginBottom:20 }}>
          {[{id:"live",label:"LIVE DRAW"},{id:"how",label:"HOW IT WORKS"}].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              fontFamily:"'Orbitron', sans-serif", fontSize:"10px", letterSpacing:"3px",
              padding:"8px 20px",
              background: activeTab===tab.id ? `linear-gradient(135deg,${COLORS.purpleDim},${COLORS.tealDim})` : "transparent",
              border:`1px solid ${activeTab===tab.id ? "rgba(153,69,255,0.5)" : "rgba(255,255,255,0.1)"}`,
              borderRadius:4, color: activeTab===tab.id ? COLORS.purple : "rgba(255,255,255,0.3)",
              cursor:"pointer", transition:"all 0.2s",
            }}>{tab.label}</button>
          ))}
        </div>

        {/* HOW IT WORKS */}
        {activeTab === "how" && (
          <div style={{ background:COLORS.cardBg, border:`1px solid ${COLORS.cardBorder}`, borderRadius:8, padding:isMobile?20:36, maxWidth:760, margin:"0 auto", animation:"fadeUp 0.3s ease", position:"relative" }}>
            <div style={{ position:"absolute",top:0,left:0,right:0,height:2, background:`linear-gradient(90deg,transparent,${COLORS.purple},${COLORS.teal},transparent)`, borderRadius:"8px 8px 0 0" }} />
            {[
              { num:"01", title:"Buy and hold $SLOTS", color:COLORS.purple, body:"Simple. Buy $SLOTS on pump.fun and keep it in your wallet. You need at least 100,000 tokens to get into the draw — that's it. No staking, no locking, no complicated steps. Just hold." },
              { num:"02", title:"Your points build over time", color:COLORS.teal, body:"Every holder gets a score based on two things: how many tokens you hold and how long you've held them. The formula is log(tokens) × days held. In plain english — a small holder who's been in for two weeks can easily outscore a whale who bought yesterday. Time matters more than bag size. Sell below 100k and your streak resets to zero." },
              { num:"03", title:"A winner gets picked every 5 minutes", color:COLORS.purple, body:"Every five minutes the bot pulls the current holder list, calculates everyone's points, and runs a weighted random draw. Higher points means better odds — but it's still random. Anyone qualifying can win. The whole thing runs automatically, no human involvement." },
              { num:"04", title:"100% of trading fees go to the pot", color:COLORS.teal, body:"Every time someone buys or sells $SLOTS on pump.fun, a creator fee gets generated. All of that goes straight into the pot — nothing held back, no team cut. The bot claims it and sends the full balance to the winner each draw. Bigger volume means bigger pots." },
              { num:"05", title:"Every payout is on-chain and verifiable", color:COLORS.purple, body:"The site shows every single transaction with the actual Solana tx hash you can look up on Solscan yourself right now. We can't fake it, we can't hide it, and we can't change it. That's the whole point of building this on-chain." },
              { num:"06", title:"Cooldowns keep it fair", color:COLORS.teal, body:"If you win, you sit out the next 6 draws (30 minutes) before you're eligible again. This stops the same wallet from sweeping the pot over and over and means more people win more often, which keeps the community actually engaged instead of watching one whale collect everything." },
            ].map((item,i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"48px 1fr", gap:isMobile?8:20, marginBottom:i<5?28:0, paddingBottom:i<5?28:0, borderBottom:i<5?"1px solid rgba(255,255,255,0.05)":"none" }}>
                <div style={{ fontFamily:"'Orbitron',sans-serif", fontSize:isMobile?"12px":"20px", fontWeight:"900", color:item.color, opacity:0.4, textShadow:`0 0 10px ${item.color}`, paddingTop:isMobile?0:2 }}>{item.num}</div>
                <div>
                  <div style={{ fontFamily:"'Orbitron',sans-serif", fontSize:"13px", fontWeight:"700", color:item.color, letterSpacing:"1px", marginBottom:8, textShadow:`0 0 8px ${item.color}` }}>{item.title}</div>
                  <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"13px", color:"rgba(255,255,255,0.5)", lineHeight:1.8, letterSpacing:"0.3px" }}>{item.body}</div>
                </div>
              </div>
            ))}
            <div style={{ marginTop:32, padding:20, background:"rgba(0,0,0,0.4)", border:`1px solid rgba(153,69,255,0.2)`, borderRadius:6, textAlign:"center" }}>
              <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:"13px", color:"rgba(255,255,255,0.4)", marginBottom:12, lineHeight:1.7 }}>
                The longer you hold, the better your odds. Every draw is live on this page and every payout is on Solscan.
              </div>
              <a href="https://pump.fun" target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", padding:"10px 28px", background:`linear-gradient(135deg,${COLORS.purpleDim},${COLORS.tealDim})`, border:`1px solid rgba(153,69,255,0.4)`, borderRadius:4, color:COLORS.purple, textDecoration:"none", fontFamily:"'Orbitron',sans-serif", fontSize:"11px", fontWeight:"700", letterSpacing:"3px" }}>
                BUY $SLOTS ON PUMP.FUN ↗
              </a>
            </div>
          </div>
        )}

        {/* Main grid */}
        {activeTab === "live" && <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 320px",
          gap: 16,
          alignItems: "start",
        }}>

          {/* LEFT: Slot machine + TX feed */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Slot machine card */}
            <div style={{
              background: COLORS.cardBg,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 8,
              padding: isMobile ? 16 : 24,
              position: "relative",
              overflow: "hidden",
            }}>
              {/* Top chrome strip */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 2,
                background: `linear-gradient(90deg, transparent, ${COLORS.purple}, ${COLORS.teal}, transparent)`,
              }} />

              {/* Machine header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
                flexWrap: "wrap",
                gap: 12,
              }}>
                <div>
                  <div style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "10px",
                    letterSpacing: "4px",
                    color: "rgba(255,255,255,0.3)",
                    marginBottom: 4,
                  }}>
                    LIVE DRAW
                  </div>
                  <div style={{
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: "11px",
                    color: spinning
                      ? COLORS.purple
                      : showWinner
                        ? COLORS.teal
                        : "rgba(255,255,255,0.4)",
                    animation: spinning ? "pulse 0.6s infinite" : "none",
                  }}>
                    {spinning ? "◆ DRAWING WINNER ◆" : showWinner ? "◆ WINNER CONFIRMED ◆" : "◆ WAITING FOR DRAW ◆"}
                  </div>
                </div>
                <CountdownRing nextDrawTime={nextDrawTime} />
              </div>

              {/* THE REELS — main feature */}
              <div style={{
                background: "rgba(0,0,0,0.7)",
                border: `1px solid ${spinning ? COLORS.purple : showWinner ? COLORS.teal : "rgba(255,255,255,0.1)"}`,
                borderRadius: 6,
                padding: isMobile ? "12px 8px" : "16px 12px",
                marginBottom: 16,
                boxShadow: spinning
                  ? `0 0 30px ${COLORS.purpleGlow}`
                  : showWinner
                    ? `0 0 30px ${COLORS.tealGlow}`
                    : "none",
                transition: "border-color 0.3s, box-shadow 0.3s",
                position: "relative",
              }}>
                {/* Machine frame decoration */}
                <div style={{
                  position: "absolute", top: 4, left: 8,
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: "8px", letterSpacing: "3px",
                  color: "rgba(255,255,255,0.15)",
                }}>
                  QUALIFYING WALLETS
                </div>

                {/* Three reels */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: isMobile ? 6 : 10,
                  marginTop: 16,
                }}>
                  {[0, 1, 2].map(i => (
                    <Reel
                      key={i}
                      spinning={spinning}
                      finalValue={finalSymbol}
                      delay={i}
                      highlight={showWinner && !spinning}
                    />
                  ))}
                </div>

                {/* Reel window chrome lines */}
                {[0, 1].map(i => (
                  <div key={i} style={{
                    position: "absolute",
                    left: 0, right: 0,
                    top: i === 0 ? "38%" : "62%",
                    height: "1px",
                    background: "rgba(255,255,255,0.04)",
                  }} />
                ))}
              </div>

              {/* Winner reveal */}
              {showWinner && winnerAddress && !spinning && (
                <div style={{
                  background: COLORS.tealDim,
                  border: `1px solid rgba(20,241,149,0.3)`,
                  borderRadius: 6,
                  padding: "14px 16px",
                  textAlign: "center",
                  animation: "fadeUp 0.4s ease",
                  marginBottom: 12,
                }}>
                  <div style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "9px",
                    letterSpacing: "4px",
                    color: "rgba(20,241,149,0.6)",
                    marginBottom: 6,
                  }}>
                    WINNER
                  </div>
                  <div style={{
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: "clamp(14px, 3vw, 18px)",
                    color: COLORS.teal,
                    fontWeight: "bold",
                    marginBottom: 4,
                    textShadow: `0 0 12px ${COLORS.teal}`,
                    animation: "winFlash 0.8s ease 3",
                  }}>
                    {winnerAddress}
                  </div>
                  {feed?.lastWinner && (
                    <div style={{
                      fontFamily: "'Orbitron', sans-serif",
                      fontSize: "22px",
                      fontWeight: "900",
                      color: COLORS.purple,
                      textShadow: `0 0 20px ${COLORS.purple}`,
                    }}>
                      +{feed.lastWinner.solAmount} SOL
                    </div>
                  )}
                </div>
              )}

              {/* Bottom chrome strip */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
                background: `linear-gradient(90deg, transparent, ${COLORS.teal}, ${COLORS.purple}, transparent)`,
              }} />
            </div>

            {/* TX Feed */}
            <div style={{
              background: COLORS.cardBg,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 8,
              padding: isMobile ? 14 : 20,
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}>
                <div style={{
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: "9px",
                  letterSpacing: "4px",
                  color: "rgba(255,255,255,0.3)",
                }}>
                  ON-CHAIN CONFIRMATIONS
                </div>
                <div style={{
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: "11px",
                  color: COLORS.teal,
                }}>
                  {totalPaid.toFixed(3)} SOL PAID
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {recentWins.length === 0 ? (
                  <div style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "rgba(255,255,255,0.2)",
                    fontSize: "12px",
                    letterSpacing: "2px",
                  }}>
                    AWAITING FIRST DRAW...
                  </div>
                ) : recentWins.map((tx, i) => (
                  <div key={i} className="tx-row" style={{
                    display: "grid",
                    gridTemplateColumns: "8px 1fr auto auto",
                    gap: isMobile ? 8 : 12,
                    alignItems: "center",
                    padding: "8px 10px",
                    background: i === 0 ? COLORS.tealDim : "transparent",
                    border: `1px solid ${i === 0 ? "rgba(20,241,149,0.2)" : "rgba(255,255,255,0.04)"}`,
                    borderRadius: 4,
                    transition: "background 0.2s",
                  }}>
                    <div style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: i === 0 ? COLORS.teal : "rgba(153,69,255,0.5)",
                      boxShadow: i === 0 ? `0 0 6px ${COLORS.teal}` : "none",
                      flexShrink: 0,
                    }} />
                    <div style={{
                      fontFamily: "'Share Tech Mono', monospace",
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.5)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {tx.shortWinner || short(tx.winner)}
                    </div>
                    <div style={{
                      fontFamily: "'Orbitron', sans-serif",
                      fontSize: "12px",
                      fontWeight: "700",
                      color: COLORS.teal,
                      whiteSpace: "nowrap",
                    }}>
                      +{tx.solAmount} SOL
                    </div>
                    <a
                      href={`https://solscan.io/tx/${tx.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontFamily: "'Share Tech Mono', monospace",
                        fontSize: "9px",
                        color: COLORS.purple,
                        textDecoration: "none",
                        letterSpacing: "1px",
                        padding: "3px 7px",
                        border: `1px solid rgba(153,69,255,0.4)`,
                        borderRadius: 3,
                        whiteSpace: "nowrap",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = COLORS.purpleDim}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      VERIFY ↗
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Leaderboard */}
          <div style={{
            background: COLORS.cardBg,
            border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 8,
            padding: isMobile ? 14 : 20,
            position: isMobile ? "static" : "sticky",
            top: 24,
          }}>
            {/* Top strip */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, transparent, ${COLORS.purple}, transparent)`,
              borderRadius: "8px 8px 0 0",
            }} />

            <div style={{
              fontFamily: "'Orbitron', sans-serif",
              fontSize: "9px",
              letterSpacing: "4px",
              color: "rgba(255,255,255,0.3)",
              textAlign: "center",
              marginBottom: 16,
              paddingBottom: 12,
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}>
              ◆ LEADERBOARD ◆
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {leaderboard.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "rgba(255,255,255,0.2)",
                  fontSize: "11px",
                  letterSpacing: "2px",
                }}>
                  AWAITING HOLDERS...
                </div>
              ) : leaderboard.map((h, i) => (
                <div key={i} className="lb-row" style={{
                  display: "grid",
                  gridTemplateColumns: "22px 1fr auto",
                  gap: 8,
                  alignItems: "center",
                  padding: "9px 10px",
                  background: i === 0
                    ? "rgba(153,69,255,0.08)"
                    : h.onCooldown
                      ? "rgba(255,255,255,0.01)"
                      : "transparent",
                  border: `1px solid ${i === 0 ? "rgba(153,69,255,0.2)" : "rgba(255,255,255,0.04)"}`,
                  borderRadius: 4,
                  opacity: h.onCooldown ? 0.5 : 1,
                  transition: "background 0.2s",
                }}>
                  <div style={{
                    fontFamily: "'Orbitron', sans-serif",
                    fontSize: "10px",
                    fontWeight: "700",
                    color: i === 0
                      ? COLORS.purple
                      : i === 1
                        ? COLORS.chrome
                        : i === 2
                          ? "#CD7F32"
                          : "rgba(255,255,255,0.2)",
                    textAlign: "center",
                  }}>
                    {i === 0 ? "◆" : i + 1}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "'Share Tech Mono', monospace",
                      fontSize: "12px",
                      color: i === 0 ? COLORS.purple : "rgba(255,255,255,0.7)",
                      marginBottom: 2,
                    }}>
                      {h.shortAddress}
                    </div>
                    <div style={{
                      fontSize: "9px",
                      color: "rgba(255,255,255,0.25)",
                      letterSpacing: "1px",
                    }}>
                      {h.onCooldown
                        ? `⏳ COOLDOWN ${h.cooldownDrawsLeft} draws`
                        : `${(h.tokens / 1000).toFixed(0)}K · ${h.daysHeld}d`
                      }
                    </div>
                  </div>
                  <div style={{
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: "10px",
                    color: h.onCooldown ? "rgba(255,255,255,0.2)" : COLORS.teal,
                    textAlign: "right",
                  }}>
                    {h.points?.toFixed(1)}
                  </div>
                </div>
              ))}
            </div>

            {/* How it works */}
            <div style={{
              marginTop: 16,
              padding: 12,
              background: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.05)",
              borderRadius: 4,
            }}>
              <div style={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: "8px",
                letterSpacing: "3px",
                color: "rgba(255,255,255,0.2)",
                marginBottom: 8,
              }}>
                HOW IT WORKS
              </div>
              {[
                ["HOLD", "100K+ $SLOTS to qualify"],
                ["EARN", "Points = log(tokens) × days held"],
                ["WIN", "Weighted random every 5 min"],
                ["STREAK", "Selling resets your points"],
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: "flex", gap: 8, marginBottom: 5,
                  fontFamily: "'Share Tech Mono', monospace",
                  fontSize: "10px",
                }}>
                  <span style={{ color: COLORS.purple, flexShrink: 0 }}>{k}</span>
                  <span style={{ color: "rgba(255,255,255,0.35)" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Buy button */}
            <a
              href="https://pump.fun"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                marginTop: 12,
                padding: "12px",
                background: `linear-gradient(135deg, ${COLORS.purpleDim}, ${COLORS.tealDim})`,
                border: `1px solid rgba(153,69,255,0.4)`,
                borderRadius: 4,
                textAlign: "center",
                color: COLORS.purple,
                textDecoration: "none",
                fontFamily: "'Orbitron', sans-serif",
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "3px",
                transition: "all 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = `linear-gradient(135deg, rgba(153,69,255,0.25), rgba(20,241,149,0.25))`;
                e.currentTarget.style.borderColor = COLORS.teal;
                e.currentTarget.style.color = COLORS.teal;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${COLORS.purpleDim}, ${COLORS.tealDim})`;
                e.currentTarget.style.borderColor = "rgba(153,69,255,0.4)";
                e.currentTarget.style.color = COLORS.purple;
              }}
            >
              BUY $SLOTS ↗
            </a>
          </div>
        </div>

        }

        {/* Footer */}
        <div style={{
          textAlign: "center",
          marginTop: 32,
          paddingTop: 16,
          borderTop: "1px solid rgba(255,255,255,0.05)",
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: "10px",
          color: "rgba(255,255,255,0.15)",
          letterSpacing: "2px",
        }}>
          SLOTSONSOL.XYZ ◆ ALL PAYOUTS VERIFIED ON-CHAIN ◆ SOLANA
        </div>
      </div>
    </div>
  );
}
